import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { EntityList } from '@/common/entityFramework/entityList/EntityList'
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * GP-9: This is an optional step in the Calc Job Dialog Wizard for MAMP, BP, and ASP calculations.
 * The user may select reallocations for the calculation
 */
export const ReallocSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep, }) => {
  const rsrc = useRsrc();

  const [reallocMode, setReallocMode] = useState(calcChainReq.reallocMode ?? 'BUNDLE');
  const [subtitle, setSubtitle] = useState();


  const liabPolType = calcChainReq.liabPolType;
  const isRepro = calcChainReq.calcJobReqs[0].src !== 'PER';

  // On init, let parent know what the next page is
  useEffect(() => {
    setNextStep('Options');
  }, []);

  const augFieldDefs = (fieldToFieldDef) => {
    const columns = ['code', 'name', 'recBy'];

    if (reallocMode === 'BUNDLE') {
      columns.push('ctrtId', 'periodicity', 'startPeriodId', 'endPeriodId');
    }

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;
  };

  const entityListRef = useRef();
  const clientSideRowData = useCallback((setDataSource) => {
    if (reallocMode === 'SOR') {
      setDataSource([]); // SOR Realloc does not require any rows to appear
      return;
    }

    if (reallocMode === 'REALLOC') {
      const expr = ExprUtils.and(
        ExprUtils.whereParams('type', 'SOR_REALLOC', 'pol_type', liabPolType.code, 'period_id', calcChainReq.calcJobReqs[0].period.id)
      );
      const rowsReq = { expr };
      rsrc.rows('gp', 'CALC', rowsReq).then((rows) => {
        setDataSource(rows);
      });
      return;
    }

    const isSingleReproWbk = isRepro && calcChainReq.calcJobReqs[0].src === 'WBK' && calcChainReq.calcJobReqs.length === 1;
    if (isSingleReproWbk) {
      const rowsReq = { 
        replaceParams: {
          wbkId: calcChainReq.calcJobReqs[0].srcId 
      }};
      
      rsrc.rows('gp', 'REPRO_SELECT_BUNDLES', rowsReq).then((bundles) => {
        setDataSource(bundles);
        const gridApi = entityListRef.current?.gridApi;
        if (!gridApi) return;

        gridApi.forEachNode(node => {
          if (bundles.find(bundle => bundle.id === node.data.id)) {
            node.setSelected(true);
          }
        });
      });
      return;
    }

    // Default, show all bundles
    const rowsReq = { expr: ExprUtils.whereParams('type', 'BUNDLE') };
    rsrc.rows('gp', 'POL', rowsReq).then((rows) => {
      setDataSource(rows);
    })
  }, [reallocMode]);

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();

    setCalcChainReq(prev => {
      const updated = { ...prev };

      if (reallocMode === 'REALLOC') {
        updated.calcJobReqs[0].reallocIds = selectedRows.map(r => r.id);
      } else if (reallocMode === 'BUNDLE') {
        const bundles = [];

        selectedRows.forEach(row => {
          const offsetVal = row.offsets;
          if (offsetVal != null) {
            let offset = offsetVal;
            while (offset <= 0) {
              bundles.push({ id: row.id, offset });
              offset++;
            }
          } else {
            bundles.push({ id: row.id, offset: 0 });
          }
        });

        updated.bundles = bundles;
      }

      return updated;
    });
  };

  // Set subtitle
  useEffect(() => {
    const subtitleMap = {
      realloc: 'Previous Reallocation',
      sor: 'Reallocation per System of Record',
      bundle: 'Please select Bundles for use in the Reallocation',
    };
    setSubtitle(subtitleMap[reallocMode]);
  }, [reallocMode]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1,
          gap: 10
        }}>
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* All Policies Toggle */}
          <ToggleButtonGroup
            value={reallocMode}
            exclusive
            onChange={(e, value) => {
              setReallocMode(value);
              setCalcChainReq(prev => {
                const updated = { ...prev };
                updated.reallocMode = value;
                updated.bundles = [];
                updated.calcJobReqs[0].reallocIds = [];
                return updated;
              });
            }}
            sx={{ height: 40, color: 'primary.main' }}>
            <ToggleButton value="REALLOC" data-test-id="toggle-REALLOC" sx={{ whiteSpace: 'nowrap' }}>
              Reallocations
            </ToggleButton>
            <ToggleButton value="SOR" data-test-id="toggle-SOR" sx={{ whiteSpace: 'nowrap' }}>
              SOR
            </ToggleButton>
            <ToggleButton value="BUNDLE" data-test-id="toggle-BUNDLE" sx={{ whiteSpace: 'nowrap' }}>
              Bundles
            </ToggleButton>
          </ToggleButtonGroup>

        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <EntityList
          key={`${reallocMode}`}
          module="gp"
          entity={reallocMode === 'REALLOC' ? 'CALC' : 'POL'}
          ref={entityListRef}
          augFieldDefs={augFieldDefs}
          clientSideRowData={clientSideRowData}
          gridOptions={{
            rowSelection: 'multiple',
          }}
          events={{
            onRowSelected: rowSelected,
          }}
        />
      </Box>
    </Box >
  )
}